import { trackEvent, trackPerformance } from './analytics';
import {
  ANALYTICS_EVENTS,
  ERROR_TYPES,
  type AnalyticsEventType,
  type NotificationType,
  type NotificationSource,
  type TourAction,
  type FormHelpAction,
} from '../constants/analytics';
import {
  WATCH_LIST_EVENTS,
  type WatchListEventProperties,
} from './eventTracking';

// Types
interface User {
  id: string | number;
  username: string;
  email: string;
  role?: string;
  canUseRealTimeNotificationsFileOwner?: boolean;
  canUseRealTimeNotificationsWatched?: boolean;
  canUseRealTimeNotificationsProperty?: boolean;
}

interface Notification {
  id: string | number;
  notification_type: NotificationType;
  notification_source: NotificationSource;
  notification_scope?: string;
  certification_id?: string | number;
  property_id?: string | number;
  household_id?: string | number;
  priority?: string;
  category?: string;
  url?: string;
  timestamp?: number;
}

interface FormContext {
  type: string;
  id: string | null;
  certificationId: string | null;
  propertyId: string | null;
  context: string;
}

interface UserPermissions {
  canUseRealTimeNotificationsFileOwner: boolean;
  canUseRealTimeNotificationsWatched: boolean;
  canUseRealTimeNotificationsProperty: boolean;
}

interface EnhancedPayload {
  user_id: string | number | null;
  username: string | null;
  user_email: string | null;
  user_role: string | null;
  user_permissions: UserPermissions;
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | object
    | Array<unknown>;
}

// Get current user from auth context or localStorage
const getCurrentUser = (): User | null => {
  try {
    // Try to get user from localStorage (fallback)
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Enhanced trackEvent with user identification
const trackEventWithUser = (
  eventName: AnalyticsEventType,
  payload: Record<
    string,
    string | number | boolean | null | undefined | object | Array<unknown>
  > = {}
): void => {
  const user = getCurrentUser();

  const enhancedPayload: EnhancedPayload = {
    ...payload,
    user_id: user?.id || null,
    username: user?.username || null,
    user_email: user?.email || null,
    user_role: user?.role || null,
    user_permissions: {
      canUseRealTimeNotificationsFileOwner:
        user?.canUseRealTimeNotificationsFileOwner || false,
      canUseRealTimeNotificationsWatched:
        user?.canUseRealTimeNotificationsWatched || false,
      canUseRealTimeNotificationsProperty:
        user?.canUseRealTimeNotificationsProperty || false,
    },
  };

  trackEvent(eventName, enhancedPayload);
};

// Notification Tracking Functions
export const trackNotificationReceived = (notification: Notification): void => {
  trackEventWithUser(ANALYTICS_EVENTS.NOTIFICATION_RECEIVED, {
    notification_id: notification.id,
    notification_type: notification.notification_type,
    notification_source: notification.notification_source,
    notification_scope: notification.notification_scope,
    certification_id: notification.certification_id,
    property_id: notification.property_id,
    household_id: notification.household_id,
    notification_priority: notification.priority || 'normal',
    notification_category: notification.category || 'general',
  });
};

export const trackNotificationClicked = (
  notification: Notification,
  action: string
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.NOTIFICATION_CLICKED, {
    notification_id: notification.id,
    notification_type: notification.notification_type,
    notification_source: notification.notification_source,
    action_type: action,
    destination_url: notification.url,
    time_to_click: Date.now() - (notification.timestamp || Date.now()),
  });
};

export const trackNotificationPolling = (
  responseTime: number,
  newCount: number,
  errorCount: number
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.NOTIFICATION_POLLING, {
    poll_interval: 15000, // 15 seconds
    response_time: responseTime,
    new_notifications_count: newCount,
    error_count: errorCount,
    polling_success: errorCount === 0,
  });
};

// Tour Tracking Functions
export const trackTourEvent = (
  tourName: string,
  action: TourAction,
  additionalData: Record<
    string,
    string | number | boolean | null | undefined | object | Array<unknown>
  > = {}
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.TOUR_INTERACTION, {
    tour_name: tourName,
    action: action,
    ...additionalData,
  });
};

export const trackTourStep = (
  tourName: string,
  stepName: string,
  stepNumber: number,
  timeOnStep: number,
  userInteraction: string
): void => {
  const eventName = `${tourName}_tour_step` as AnalyticsEventType;
  trackEventWithUser(eventName, {
    step_name: stepName,
    step_number: stepNumber,
    time_on_step: timeOnStep,
    user_interaction: userInteraction,
    tour_name: tourName,
  });
};

export const trackTourNavigation = (
  fromTour: string,
  toTour: string,
  navigationMethod: string
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.TOUR_NAVIGATION, {
    from_tour: fromTour,
    to_tour: toTour,
    navigation_method: navigationMethod,
  });
};

// Watch List Tracking Functions
export const trackWatchListUpdate = (
  action: string,
  certIds: (string | number)[],
  count: number,
  limit: number
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.WATCH_LIST_UPDATED, {
    action: action,
    certification_ids: certIds,
    certification_count: count,
    watch_list_limit: limit,
    watch_list_usage_percentage: (count / limit) * 100,
  });
};

// New dedicated watch list tracking functions
export const trackWatchListAdd = (
  source: string,
  certIds: number[],
  context: {
    property_id?: number;
    household_id?: number;
    household_name?: string;
  } = {}
): void => {
  const eventProperties: WatchListEventProperties = {
    action: 'add_to_watchlist',
    source,
    certification_ids: certIds,
    certification_count: certIds.length,
    ...context,
  };

  trackEvent(WATCH_LIST_EVENTS.WATCH_LIST_ADD, eventProperties);
};

export const trackWatchListRemove = (
  source: string,
  certIds: number[],
  context: {
    property_id?: number;
    household_id?: number;
    household_name?: string;
  } = {}
): void => {
  const eventProperties: WatchListEventProperties = {
    action: 'remove_from_watchlist',
    source,
    certification_ids: certIds,
    certification_count: certIds.length,
    ...context,
  };

  trackEvent(WATCH_LIST_EVENTS.WATCH_LIST_REMOVE, eventProperties);
};

export const trackWatchListRemoveAll = (
  source: string,
  totalCount: number
): void => {
  const eventProperties: WatchListEventProperties = {
    action: 'remove_all_from_watchlist',
    source,
    total_certifications_removed: totalCount,
  };

  trackEvent(WATCH_LIST_EVENTS.WATCH_LIST_REMOVE_ALL, eventProperties);
};

// Settings Tracking Functions
export const trackSettingsUpdate = (
  settingName: string,
  oldValue: string | number | boolean | null | undefined,
  newValue: string | number | boolean | null | undefined
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.SETTINGS_UPDATED, {
    setting_name: settingName,
    old_value: oldValue,
    new_value: newValue,
    setting_category: getSettingCategory(settingName),
  });
};

// Navigation Tracking Functions
export const trackNavigationClick = (
  navigationItem: string,
  previousPage: string,
  destinationPage: string
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
    navigation_section: 'my_workbench',
    navigation_item: navigationItem,
    navigation_level: 'primary',
    previous_page: previousPage,
    destination_page: destinationPage,
  });
};

// Form Help Tracking Functions
export const trackFormHelpAccess = (
  formType: string,
  formId: string | number,
  certificationId: string | number | null,
  propertyId: string | number | null,
  helpContext: string,
  accessMethod: string
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.FORM_HELP_ACCESSED, {
    form_type: formType,
    form_id: formId,
    certification_id: certificationId,
    property_id: propertyId,
    help_context: helpContext,
    access_method: accessMethod,
  });
};

export const trackFormHelpInteraction = (
  helpSection: string,
  helpTopic: string,
  interactionType: FormHelpAction,
  timeSpent: number,
  formContext: FormContext
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.FORM_HELP_INTERACTION, {
    help_section: helpSection,
    help_topic: helpTopic,
    interaction_type: interactionType,
    time_spent: timeSpent,
    form_context: formContext,
  });
};

// Error Tracking Functions
export const trackApiError = (
  endpoint: string,
  statusCode: number,
  errorMessage: string,
  retryCount: number
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.NOTIFICATION_API_ERROR, {
    error_type: ERROR_TYPES.FETCH_FAILED,
    endpoint: endpoint,
    status_code: statusCode,
    error_message: errorMessage,
    retry_count: retryCount,
  });
};

export const trackUiError = (
  componentName: string,
  errorMessage: string,
  userAction: string
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.NOTIFICATION_UI_ERROR, {
    error_type: ERROR_TYPES.CLIENT_ERROR,
    component_name: componentName,
    error_message: errorMessage,
    user_action: userAction,
  });
};

export const trackWatchListError = (
  action: string,
  errorMessage: string,
  certIds: (string | number)[]
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.WATCH_LIST_ERROR, {
    error_type: ERROR_TYPES.VALIDATION_ERROR,
    action: action,
    error_message: errorMessage,
    certification_ids: certIds,
  });
};

export const trackTourSystemError = (
  tourName: string,
  errorMessage: string,
  stepNumber: number
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.TOUR_SYSTEM_ERROR, {
    error_type: ERROR_TYPES.CLIENT_ERROR,
    tour_name: tourName,
    error_message: errorMessage,
    step_number: stepNumber,
  });
};

export const trackSettingsUpdateError = (
  settingName: string,
  errorMessage: string,
  attemptedValue: string | number | boolean | null | undefined
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.SETTINGS_UPDATE_ERROR, {
    error_type: ERROR_TYPES.VALIDATION_ERROR,
    setting_name: settingName,
    error_message: errorMessage,
    attempted_value: attemptedValue,
  });
};

// Cross-tab Synchronization Tracking
export const trackCrossTabSync = (
  syncType: string,
  success: boolean,
  dataSize: number
): void => {
  trackEventWithUser(ANALYTICS_EVENTS.CROSS_TAB_SYNC, {
    sync_type: syncType,
    success: success,
    data_size: dataSize,
    timestamp: new Date().toISOString(),
  });
};

// Performance Tracking Functions
export const trackNotificationResponseTime = (
  responseTime: number,
  endpoint: string
): void => {
  trackPerformance('notification_response_time', responseTime);
  trackEventWithUser('notification_performance' as AnalyticsEventType, {
    metric: 'response_time',
    value: responseTime,
    endpoint: endpoint,
  });
};

export const trackPageLoadTime = (pageName: string, loadTime: number): void => {
  trackPerformance('page_load_time', loadTime);
  trackEventWithUser('page_performance' as AnalyticsEventType, {
    metric: 'load_time',
    value: loadTime,
    page_name: pageName,
  });
};

// Utility Functions
const getSettingCategory = (settingName: string): string => {
  const categories: Record<string, string[]> = {
    notification: [
      'email_notifications',
      'in_app_notifications',
      'notification_frequency',
    ],
    communication: ['email_preferences', 'communication_frequency'],
    privacy: ['data_sharing', 'privacy_settings'],
    display: ['theme', 'language', 'timezone'],
    general: ['profile', 'account'],
  };

  for (const [category, settings] of Object.entries(categories)) {
    if (settings.some((setting) => settingName.includes(setting))) {
      return category;
    }
  }
  return 'general';
};

// Get current form context from URL
export const getCurrentFormContext = (): FormContext => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const pathSegments = window.location.pathname.split('/');

    return {
      type: pathSegments[3] || 'unknown', // e.g., 'automated-test-apartments'
      id: pathSegments[4] || null, // e.g., '263104'
      certificationId: urlParams.get('certification_id'),
      propertyId: pathSegments[4] || null,
      context: urlParams.get('t1') || 'general', // e.g., 'file manager'
    };
  } catch (error) {
    return {
      type: 'unknown',
      id: null,
      certificationId: null,
      propertyId: null,
      context: 'general',
    };
  }
};
